import { httpsCallable } from 'firebase/functions';
import { functions } from '../config/firebase';
import { ExtractedFuelData } from '../types/fuelConsumption';
import { MaintenanceType } from '../types/maintenance';

export interface ExtractedMaintenanceData {
  date?: Date;
  costHT?: number;
  costTTC?: number;
  type?: MaintenanceType;
  description?: string;
  detailedOperations?: {
    parts?: Array<{
      name: string;
      quantity?: number;
      unitPrice?: number;
    }>;
    labor?: Array<{
      description: string;
      hours?: number;
      cost?: number;
    }>;
  };
  serviceProvider?: {
    name?: string;
    address?: string;
    phone?: string;
    email?: string;
  };
  serviceProviderId?: string;
  vehicleInfo?: {
    licensePlate?: string;
    mileage?: number;
    make?: string;
    model?: string;
  };
}

class AIService {
  private readonly maxRetries = 3;

  constructor() {
    // No initialization needed - using Firebase Functions
  }

  private async retryOperation<T>(operation: () => Promise<T>): Promise<T> {
    let lastError: Error | null = null;
    const maxAttempts = this.maxRetries;
    const baseDelay = 1000; // 1 second base delay

    for (let attempt = 0; attempt < maxAttempts; attempt++) {
      try {
        // Create a timeout promise
        const timeoutPromise = new Promise((_, reject) => {
          setTimeout(() => {
            reject(new Error('Request timeout'));
          }, 120000); // 120 second timeout
        });

        // Race between the operation and the timeout
        const result = await Promise.race([
          operation(),
          timeoutPromise
        ]) as T;
        return result;
      } catch (error: any) {
        console.error(`API request failed (attempt ${attempt + 1}/${maxAttempts}):`, error);
        lastError = error;

        // Check if we should retry based on the error type
        const shouldRetry = 
          error.name === 'TypeError' || 
          error.message.includes('network') ||
          error.message.includes('timeout') ||
          error.message.includes('ERR_BLOCKED') ||
          (error.status >= 500 && error.status < 600);

        if (shouldRetry && attempt < maxAttempts - 1) {
          // Exponential backoff with jitter
          const delay = Math.min(baseDelay * Math.pow(2, attempt) + Math.random() * 1000, 10000);
          console.log(`Retrying in ${Math.round(delay)}ms...`);
          await new Promise(resolve => setTimeout(resolve, delay));
          continue;
        }

        throw error;
      }
    }

    throw lastError || new Error('Operation failed after all retries');
  }

  async validateMileageReading(
    newMileage: number,
    currentMileage: number,
    lastReportDate: Date
  ): Promise<{ isValid: boolean; reason?: string }> {
    // Basic validation rules
    if (newMileage < currentMileage) {
      return {
        isValid: false,
        reason: 'New mileage cannot be less than current mileage'
      };
    }

    // Calculate daily mileage difference
    const daysDiff = Math.max(1, Math.floor((new Date().getTime() - lastReportDate.getTime()) / (1000 * 60 * 60 * 24)));
    const mileageDiff = newMileage - currentMileage;
    const dailyAverage = mileageDiff / daysDiff;

    // Threshold for suspicious daily mileage (e.g., more than 1000 km per day)
    const SUSPICIOUS_DAILY_THRESHOLD = 1000;

    if (dailyAverage > SUSPICIOUS_DAILY_THRESHOLD) {
      return {
        isValid: false,
        reason: `Suspicious daily average of ${Math.round(dailyAverage)} km/day`
      };
    }

    return { isValid: true };
  }

  async extractMileageFromImage(imageBase64: string): Promise<number | null> {
    return this.retryOperation(async () => {
      const extractMileage = httpsCallable<{ imageBase64: string }, { mileage: number | null }>(
        functions,
        'extractMileage'
      );

      const result = await extractMileage({ imageBase64 });
      return result.data.mileage;
    });
  }

  async extractFuelDataFromReceipt(imageBase64: string): Promise<ExtractedFuelData | null> {
    return this.retryOperation(async () => {
      const extractFuelData = httpsCallable<
        { imageBase64: string },
        { data: { date: string | null; amount: number | null; volume: number | null; supplier: string | null } | null }
      >(functions, 'extractFuelData');

      const result = await extractFuelData({ imageBase64 });

      if (!result.data.data) return null;

      return {
        date: result.data.data.date ? new Date(result.data.data.date) : undefined,
        amount: result.data.data.amount ?? undefined,
        volume: result.data.data.volume ?? undefined,
        supplier: result.data.data.supplier ?? undefined
      };
    });
  }

  async extractMaintenanceDataFromInvoice(imageBase64: string): Promise<ExtractedMaintenanceData | null> {
    return this.retryOperation(async () => {
      const extractMaintenanceData = httpsCallable<
        { imageBase64: string },
        { data: any | null }
      >(functions, 'extractMaintenanceData');

      const result = await extractMaintenanceData({ imageBase64 });

      if (!result.data.data) return null;

      const extractedData = result.data.data;

      return {
        date: extractedData.date ? new Date(extractedData.date) : undefined,
        costHT: typeof extractedData.costHT === 'number' ? extractedData.costHT : undefined,
        costTTC: typeof extractedData.costTTC === 'number' ? extractedData.costTTC : undefined,
        type: extractedData.type as MaintenanceType,
        description: extractedData.description,
        detailedOperations: {
          parts: extractedData.detailedOperations?.parts,
          labor: extractedData.detailedOperations?.labor
        },
        serviceProvider: {
          name: extractedData.serviceProvider?.name,
          address: extractedData.serviceProvider?.address,
          phone: extractedData.serviceProvider?.phone,
          email: extractedData.serviceProvider?.email
        },
        vehicleInfo: {
          licensePlate: extractedData.vehicleInfo?.licensePlate,
          mileage: typeof extractedData.vehicleInfo?.mileage === 'number'
            ? extractedData.vehicleInfo.mileage
            : undefined,
          make: extractedData.vehicleInfo?.make,
          model: extractedData.vehicleInfo?.model
        }
      };
    });
  }
}

export const aiService = new AIService();
